import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  formatDate,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import {
  GetEnrollmentAnnuityPolicyRes,
  getGetEnrollmentAnnuityPoliciesQueryKey,
  useDeleteEnrollmentAnnuityPolicy,
  useGetEnrollmentAnnuityPolicies
} from '@ijoin/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Card, CardContent, CardHeader, IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC, useState } from 'react';

import { ApiError } from '../types';
import { EnrollmentAnnuityPolicyDetailsDialog } from './EnrollmentAnnuityPolicyDetailsDialog';
import { EnrollmentAnnuityPolicyEditDialog } from './EnrollmentAnnuityPolicyEditDialog';

const DATE_ONLY_UTC = { format: 'MM/DD/YYYY', useUtc: true } as const;

export const EnrollmentAnnuityPoliciesSection: FC<{
  planEnrollmentId: string;
  planId: string;
}> = props => {
  const [selected, setSelected] =
    useState<GetEnrollmentAnnuityPolicyRes | null>(null);
  const [editing, setEditing] = useState<GetEnrollmentAnnuityPolicyRes | null>(
    null
  );
  const [deleting, setDeleting] =
    useState<GetEnrollmentAnnuityPolicyRes | null>(null);

  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const policies = useGetEnrollmentAnnuityPolicies(
    props.planId,
    props.planEnrollmentId
  );

  const deletePolicy = useDeleteEnrollmentAnnuityPolicy<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to delete annuity policy'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEnrollmentAnnuityPoliciesQueryKey(
            props.planId,
            props.planEnrollmentId
          )
        });
        notifications.success('Annuity policy deleted');
      }
    }
  });

  const onCloseDetails = () => setSelected(null);

  const onCloseEdit = () => setEditing(null);

  const onCancelDelete = () => setDeleting(null);

  const onConfirmDelete = () => {
    if (!deleting) return;

    deletePolicy.mutate({
      annuityPolicyParticipantId: deleting.id,
      planEnrollmentId: props.planEnrollmentId,
      planId: props.planId
    });
    setDeleting(null);
  };

  if (!policies.data?.length) {
    return null;
  }

  return (
    <Card sx={{ p: 1 }}>
      <CardHeader
        subheader='Annuity program accounts held by this participant in the plan'
        title='Annuity Policies'
      />
      <CardContent>
        <DataGrid
          columns={[
            {
              align: 'center',
              disableColumnMenu: true,
              display: 'flex',
              field: 'details',
              filterable: false,
              headerName: '',
              renderCell: params => (
                <IconButton
                  aria-label='Details'
                  data-testid={`annuityPolicyDetails-${params.row.annuityAccountType}`}
                  onClick={() => setSelected(params.row)}>
                  <InfoOutlinedIcon />
                </IconButton>
              ),
              sortable: false,
              width: 65
            },
            {
              field: 'annuityAccountType',
              flex: 1,
              headerName: 'Account Type',
              minWidth: 130
            },
            {
              field: 'annuityProgramStatus',
              flex: 1,
              headerName: 'Status',
              minWidth: 120,
              renderCell: params => (
                <StatusBadge
                  severity={
                    params.value?.toLowerCase() === 'active'
                      ? 'success'
                      : params.row.annuityProgramExitDate
                        ? 'warning'
                        : 'info'
                  }>
                  {params.value}
                </StatusBadge>
              )
            },
            {
              field: 'annuityTraditionalAccountId',
              flex: 1,
              headerName: 'Traditional Account ID',
              minWidth: 170
            },
            {
              field: 'annuityRothAccountId',
              flex: 1,
              headerName: 'Roth Account ID',
              minWidth: 140
            },
            {
              field: 'annuityProgramOriginType',
              flex: 1,
              headerName: 'Origin',
              minWidth: 120
            },
            {
              field: 'annuityProgramEntryDate',
              flex: 1,
              headerName: 'Entry Date',
              minWidth: 120,
              valueFormatter: value => (value ? formatDate(value) : '')
            },
            {
              field: 'eligibleForAccountOnDate',
              flex: 1,
              headerName: 'Eligible On',
              minWidth: 120,
              valueFormatter: value =>
                value ? formatDate(value, DATE_ONLY_UTC) : ''
            },
            {
              field: 'initialFundingDate',
              flex: 1,
              headerName: 'Initial Funding',
              minWidth: 130,
              valueFormatter: value =>
                value ? formatDate(value, DATE_ONLY_UTC) : ''
            },
            {
              field: 'lastSweepDate',
              flex: 1,
              headerName: 'Last Sweep',
              minWidth: 120,
              valueFormatter: value =>
                value ? formatDate(value, DATE_ONLY_UTC) : ''
            },
            {
              field: 'nextSweepDate',
              flex: 1,
              headerName: 'Next Sweep',
              minWidth: 120,
              valueFormatter: value =>
                value ? formatDate(value, DATE_ONLY_UTC) : ''
            },
            {
              field: 'currentSweepPercentage',
              flex: 1,
              headerName: 'Sweep %',
              minWidth: 100
            },
            {
              field: 'annuityProgramExitDate',
              flex: 1,
              headerName: 'Exit Date',
              minWidth: 120,
              valueFormatter: value => (value ? formatDate(value) : '')
            },
            {
              field: 'annuityProgramExitReason',
              flex: 1,
              headerName: 'Exit Reason',
              minWidth: 160
            },
            {
              field: 'productId',
              flex: 1,
              headerName: 'Product ID',
              minWidth: 160
            },
            {
              field: 'managedAccountProgramId',
              flex: 1,
              headerName: 'MAP Program ID',
              minWidth: 160
            },
            {
              align: 'center',
              field: 'actions',
              headerAlign: 'center',
              headerName: '',
              renderCell: params => (
                <ActionMenu
                  actions={[
                    {
                      content: 'Delete',
                      destructive: true,
                      onSelect: () => setDeleting(params.row)
                    },
                    {
                      content: 'Edit',
                      onSelect: () => setEditing(params.row)
                    }
                  ]}
                  trigger={
                    <IconButton
                      data-testid={`annuityPolicyActions-${params.row.annuityAccountType}`}>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
              ),
              sortable: false,
              width: 65
            }
          ]}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.id}
          loading={policies.isFetching}
          pageSizeOptions={[25, 50, 100]}
          pinnedColumns={{
            left: ['details', 'annuityAccountType'],
            right: ['actions']
          }}
          rows={policies.data}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'enrollmentAnnuityPolicies'
            }
          }}
        />
        {selected && (
          <EnrollmentAnnuityPolicyDetailsDialog
            isOpen={!!selected}
            onClose={onCloseDetails}
            policy={selected}
          />
        )}
        {editing && (
          <EnrollmentAnnuityPolicyEditDialog
            onClose={onCloseEdit}
            planEnrollmentId={props.planEnrollmentId}
            planId={props.planId}
            policy={editing}
          />
        )}
        {deleting && (
          <ConfirmationDialog
            confirmColor='error'
            confirmText='Delete'
            data-testid='deleteAnnuityPolicy'
            isOpen={!!deleting}
            message={`This will permanently delete the ${deleting.annuityAccountType} annuity policy record for this participant. The carrier account itself is not affected.`}
            onCancel={onCancelDelete}
            onConfirm={onConfirmDelete}
            title='Delete Annuity Policy'
          />
        )}
      </CardContent>
    </Card>
  );
};

EnrollmentAnnuityPoliciesSection.displayName =
  'EnrollmentAnnuityPoliciesSection';
